import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, ArrowPathIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import AuthShell from './AuthShell';
import { signedInDestination } from '../../utils/authRedirect';

const Login = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '' });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const result = await login(formData.username.trim(), formData.password);

    if (result.success) {
      toast.success('Welcome back');
      navigate(signedInDestination(params.get('returnTo')), { replace: true });
    } else {
      toast.error(result.error || 'Unable to sign in');
    }

    setLoading(false);
  };

  return (
    <AuthShell
      eyebrow="Welcome back"
      title="Sign in to your workspace"
      description="Pick up your conversations, goals and ongoing work."
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="username" className="field-label">Username</label>
          <input
            id="username"
            name="username"
            type="text"
            autoComplete="username"
            required
            value={formData.username}
            onChange={(event) => setFormData((current) => ({ ...current, username: event.target.value }))}
            className="field-input"
            placeholder="Enter your username"
          />
        </div>

        <div>
          <div className="flex items-center justify-between gap-4">
            <label htmlFor="password" className="field-label">Password</label>
            <Link to="/forgot-password" className="mb-1 text-xs font-bold text-[#246b52] hover:text-[#18513d]">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={formData.password}
              onChange={(event) => setFormData((current) => ({ ...current, password: event.target.value }))}
              className="field-input pr-12"
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute right-1 top-1 flex h-10 w-10 items-center justify-center rounded-md text-[#6d7871] hover:bg-[#edf1ed]"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <button type="submit" disabled={loading} className="primary-button w-full disabled:cursor-not-allowed disabled:opacity-60">
          {loading ? (
            <>
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Signing in
            </>
          ) : (
            <>
              Sign in
              <ArrowRightIcon className="h-4 w-4" />
            </>
          )}
        </button>
      </form>

      <div className="mt-7 border-t border-[#dce3dd] pt-6 text-center text-sm text-[#65716a]">
        New to Timelyzer?{' '}
        <Link to="/register" className="font-bold text-[#246b52] hover:text-[#18513d]">
          Create an account
        </Link>
      </div>
    </AuthShell>
  );
};

export default Login;
